import React from "react";
import {
  Box,
  Button,
  Callout,
  Flex,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Order } from "@/shared/api/lod-api";
import { SourceOption } from "@/features/annotation/lib/types";

import CustomDatePicker from "@/shared/ui/date-time-picker/date-time-picker";
import { createStageStatusOptions } from "@/features/assignment/lib";
interface FilterSidebarProps {
  isFiltersApplied: boolean;
  orderId: string;
  onOrderIdChange: (value: string) => void;
  sourceList: SourceOption[];
  source: Order["order_source"];
  fromDate: Date | null;
  toDate: Date | null;
  onSourceChange: (value: string) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  assigneeList: SourceOption[];
  assignee: string;
  onAssigneeChange: (value: string) => void;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  hasAssignmentOccurred?: boolean;
  stageStatusOptions?: string[];
  stageJourneyAnnotation: string;
  stageTransactionAnnotation: string;
  stageTrackPersistenceAnnotation: string;
  onStageJourneyAnnotationChange: (value: string) => void;
  onStageTransactionAnnotationChange: (value: string) => void;
  onStageTrackPersistenceAnnotationChange: (value: string) => void;
}

export const FilterSection: React.FC<FilterSidebarProps> = ({
  isFiltersApplied,
  orderId,
  onOrderIdChange,
  sourceList,
  source,
  fromDate,
  toDate,
  onSourceChange,
  applyFilters,
  clearFilters,
  assigneeList = [],
  assignee = "all",
  onAssigneeChange,
  onFromDateChange,
  onToDateChange,
  hasAssignmentOccurred = false,
  stageStatusOptions: apiStageStatusOptions,
  stageJourneyAnnotation,
  stageTransactionAnnotation,
  stageTrackPersistenceAnnotation,
  onStageJourneyAnnotationChange,
  onStageTransactionAnnotationChange,
  onStageTrackPersistenceAnnotationChange,
}) => {
  const stageStatusOptions = createStageStatusOptions(apiStageStatusOptions);

  return (
    <Box className="p-4 min-w-[270px] w-[270px]">
      <Flex direction="column" gap="2">
        <Text size="3" weight="bold">
          Filter
        </Text>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Order ID
          </Text>
          <TextField.Root
            placeholder="Search by order ID"
            value={orderId}
            onChange={(e) => onOrderIdChange(e.target.value)}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
          </TextField.Root>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Order Source
          </Text>
          <Box>
            <Select.Root
              defaultValue="all"
              value={source}
              onValueChange={onSourceChange}
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                <Select.Group>
                  <Select.Item value="all">All</Select.Item>
                  <Select.Separator />
                  {sourceList?.length
                    ? sourceList.map(({ key, value }) => (
                        <Select.Item key={key} value={key}>
                          {value}
                        </Select.Item>
                      ))
                    : null}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Box>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Current Assignee
          </Text>
          <Box>
            <Select.Root
              defaultValue="all"
              value={assignee}
              onValueChange={onAssigneeChange}
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                <Select.Group>
                  <Select.Item value="all">All</Select.Item>
                  <Select.Separator />
                  {assigneeList?.length
                    ? assigneeList.map(({ key, value }) => (
                        <Select.Item key={key} value={key}>
                          {value}
                        </Select.Item>
                      ))
                    : null}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Box>
        </Box>

        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Journey Annotation
          </Text>
          <Select.Root
            value={stageJourneyAnnotation || ""}
            onValueChange={(value) => onStageJourneyAnnotationChange(value)}
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.key} value={option.key}>
                  {option.value}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Transaction Annotation
          </Text>
          <Select.Root
            value={stageTransactionAnnotation || ""}
            onValueChange={(value) => onStageTransactionAnnotationChange(value)}
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.key} value={option.key}>
                  {option.value}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Box>
          <Text as="p" size="2" weight="medium" className="mb-1">
            Stage Track Persistence Annotation
          </Text>
          <Select.Root
            value={stageTrackPersistenceAnnotation || ""}
            onValueChange={(value) =>
              onStageTrackPersistenceAnnotationChange(value)
            }
          >
            <Select.Trigger className="w-full"></Select.Trigger>
            <Select.Content>
              <Select.Item value="all">All</Select.Item>
              <Select.Separator />
              {stageStatusOptions?.map((option) => (
                <Select.Item key={option.key} value={option.key}>
                  {option.value}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Date Range
          </Text>
          <Flex justify="between" align="center">
            <Box width="40px">
              <Text size="2">From:</Text>
            </Box>
            <Flex className="flex-1">
              <CustomDatePicker
                selected={fromDate}
                onChange={onFromDateChange}
              />
            </Flex>
          </Flex>
          <Flex justify="between" align="center">
            <Box width="40px">
              <Text size="2">To:</Text>
            </Box>
            <Flex className="flex-1">
              <CustomDatePicker selected={toDate} onChange={onToDateChange} />
            </Flex>
          </Flex>
        </Flex>
        <Flex gap="2" mt="1">
          <Button onClick={clearFilters} variant="outline" className="flex-1">
            Clear
          </Button>
          <Button
            onClick={applyFilters}
            disabled={isFiltersApplied}
            className="flex-1"
          >
            Apply
          </Button>
        </Flex>
        {hasAssignmentOccurred && (
          <Callout.Root color="amber" variant="soft" size="1" mt="2">
            <Callout.Icon>
              <InfoCircledIcon />
            </Callout.Icon>
            <Callout.Text>
              Data has been updated. Apply filters to refresh the view.
            </Callout.Text>
          </Callout.Root>
        )}
      </Flex>
    </Box>
  );
};
